/**
 * Age-based developmental milestone predictions — what to watch for at each age.
 * Based on CDC "Learn the Signs. Act Early." developmental milestones (2022 update).
 * Not a diagnosis — every baby develops on their own timeline. Cite pediatrician.
 */

export interface DevMilestone {
  category: 'social' | 'language' | 'cognitive' | 'motor'
  text: string
}

export interface DevWindow {
  ageMonths: number
  headline: string           // "What to watch for around 2 months"
  milestones: DevMilestone[]
  redFlags?: string[]        // things to raise with pediatrician
}

export const DEV_MILESTONES: DevWindow[] = [
  {
    ageMonths: 2,
    headline: '2 months',
    milestones: [
      { category: 'social',    text: 'Smiles when spoken to' },
      { category: 'social',    text: 'Calms when held or spoken to' },
      { category: 'language',  text: 'Makes cooing sounds' },
      { category: 'language',  text: 'Reacts to loud sounds' },
      { category: 'cognitive', text: 'Watches you as you move' },
      { category: 'motor',     text: 'Holds head up during tummy time' },
    ],
    redFlags: ['Doesn\'t respond to loud sounds', 'Doesn\'t follow moving things with eyes'],
  },
  {
    ageMonths: 4,
    headline: '4 months',
    milestones: [
      { category: 'social',    text: 'Smiles to get your attention' },
      { category: 'social',    text: 'Laughs' },
      { category: 'language',  text: 'Makes cooing sounds like "oooo"' },
      { category: 'cognitive', text: 'Opens mouth for breast/bottle when sees it' },
      { category: 'motor',     text: 'Holds head steady without support' },
      { category: 'motor',     text: 'Brings hands to mouth' },
    ],
    redFlags: ['Doesn\'t make sounds other than crying', 'Doesn\'t hold head up when propped'],
  },
  {
    ageMonths: 6,
    headline: '6 months',
    milestones: [
      { category: 'social',    text: 'Knows familiar people' },
      { category: 'social',    text: 'Likes to look at self in mirror' },
      { category: 'language',  text: 'Takes turns making sounds with you' },
      { category: 'language',  text: 'Sticks tongue out + blows raspberries' },
      { category: 'cognitive', text: 'Puts things in mouth to explore' },
      { category: 'motor',     text: 'Rolls from tummy to back' },
      { category: 'motor',     text: 'Pushes up with straight arms during tummy time' },
    ],
    redFlags: ['Doesn\'t roll over in either direction', 'Doesn\'t laugh or make squealing sounds'],
  },
  {
    ageMonths: 9,
    headline: '9 months',
    milestones: [
      { category: 'social',    text: 'Shy or anxious around strangers' },
      { category: 'social',    text: 'Shows facial expressions like happy, sad, angry' },
      { category: 'language',  text: 'Makes different sounds like "mamamama"' },
      { category: 'cognitive', text: 'Looks for objects you hide' },
      { category: 'motor',     text: 'Sits without support' },
      { category: 'motor',     text: 'Passes things from one hand to other' },
    ],
    redFlags: ['Doesn\'t sit with help', 'Doesn\'t respond to own name'],
  },
  {
    ageMonths: 12,
    headline: '12 months (1 year)',
    milestones: [
      { category: 'social',    text: 'Plays games like pat-a-cake' },
      { category: 'language',  text: 'Waves "bye-bye"' },
      { category: 'language',  text: 'Calls a parent "mama" or "dada"' },
      { category: 'cognitive', text: 'Puts something in a container' },
      { category: 'motor',     text: 'Pulls up to stand' },
      { category: 'motor',     text: 'Walks holding on to furniture (cruising)' },
    ],
    redFlags: ['Doesn\'t crawl', 'Doesn\'t say single words like "mama" or "dada"'],
  },
  {
    ageMonths: 15,
    headline: '15 months',
    milestones: [
      { category: 'social',    text: 'Claps when excited' },
      { category: 'language',  text: 'Tries to say one or two words besides mama/dada' },
      { category: 'cognitive', text: 'Tries to use things the right way (phone, cup)' },
      { category: 'motor',     text: 'Takes a few steps on own' },
    ],
  },
  {
    ageMonths: 18,
    headline: '18 months',
    milestones: [
      { category: 'social',    text: 'Points to show you something interesting' },
      { category: 'language',  text: 'Tries to say 3+ words besides mama/dada' },
      { category: 'cognitive', text: 'Copies you doing chores like sweeping' },
      { category: 'motor',     text: 'Walks without holding on to anyone' },
      { category: 'motor',     text: 'Scribbles with a crayon' },
    ],
    redFlags: ['Doesn\'t point to show things', 'Doesn\'t walk'],
  },
  {
    ageMonths: 24,
    headline: '2 years',
    milestones: [
      { category: 'social',    text: 'Notices when others are hurt or upset' },
      { category: 'language',  text: 'Says at least 2 words together' },
      { category: 'cognitive', text: 'Holds something in one hand while using the other' },
      { category: 'motor',     text: 'Runs' },
      { category: 'motor',     text: 'Kicks a ball' },
    ],
    redFlags: ['Doesn\'t use 2-word phrases', 'Doesn\'t follow simple instructions'],
  },
]

/** Get the current + next milestone windows for a baby based on age. */
export function nextDevWindows(ageMonths: number, limit = 2): DevWindow[] {
  if (ageMonths < 0 || ageMonths > 30) return []
  const upcoming = DEV_MILESTONES.filter((w) => w.ageMonths >= ageMonths - 0.5)
  if (upcoming.length === 0) return []
  return upcoming.slice(0, limit)
}
